<script lang="ts">
  import { businessWallClock, businessWallClockToRFC3339 } from '$lib/utils/businessTime';
  // Directory > person detail — the merge of the old HR employee-detail page
  // (Profile/Attendance/Leave/Board/History, HR+admin with server-side field
  // filtering) and the Continuity module's PersonDetail (Someone is leaving/
  // Dates/OKRs/What they hold/Conversations/Actions, admin-only). One page,
  // one URL (#/directory/person/:id) — the Continuity section below simply
  // doesn't render, and its queries never fire, for hr sessions.
  //
  // Layout: a sticky identity header (who this is, at a glance, plus the
  // window selector), then stacked, individually-anchored sections. There is
  // deliberately no in-page tab bar — every section is always rendered and
  // always reachable by scrolling; the anchor rail under the header only
  // scrolls to a section, it never hides one.
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { SvelteSet } from 'svelte/reactivity';
  import {
    MapPin,
    Wrench,
    RotateCcw,
    Upload,
    Download,
    Trash2,
    Plus,
    KeyRound,
    Smartphone,
    ShieldOff,
    Mail,
    Phone,
    Building2
  } from 'lucide-svelte';
  import {
    hrAnalyticsApi,
    hrProfileApi,
    hrAttendanceApi,
    hrWorkZonesApi,
    hrAuditApi,
    hrLeaveApi,
    hrInboxApi,
    hrUsersApi,
    hrUserFlagsApi,
    type HRInsightRangeKey,
    type HRManagedRequestBody
  } from '$lib/api/hr';
  import { hrDevicesApi } from '$lib/api/devices';
  import { adminBoardApi } from '$lib/api/admin';
  import { peopleApi } from '$lib/api/people';
  import { okrsApi } from '$lib/api/okrs';
  import { custodyApi } from '$lib/api/custody';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import { leafletMap, type LeafletMapParams, type MapPoint, type MapCircle } from '$lib/utils/leafletMap';
  import { humanizeAuditDetails, AUDIT_ACTION_LABEL } from '$lib/utils/audit';
  import { copyText, formatPersonDetails } from '$lib/utils/personDetails';
  import { todayISO } from '$lib/utils/appDate';
  import Modal from '$lib/components/ui/Modal.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import BarRow from '$lib/components/ui/BarRow.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import StatusBadge, { type BadgeKind } from '$lib/components/ui/StatusBadge.svelte';
  import InfoTip from '$lib/components/ui/InfoTip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import AttendanceInsightCard from './AttendanceInsightCard.svelte';
  import type {
    HRFlag,
    HRInsightCard,
    HRAnalyticsRecentWorkLog,
    HRAnalyticsFailedAttempt,
    HRAnalyticsRecentRequest,
    HRRequestType,
    EmployeeProfileUpdate,
    EmploymentType,
    Gender,
    EmployeeDocument,
    ContinuityActionRow,
    CustodyRow,
    CustodyType,
    OKRColour,
    OKRRef,
    PayBandPosition,
    ConversationType
  } from '$lib/types';

  const { userId, onJumpToOKR }: { userId: number; onJumpToOKR: (id: number) => void } = $props();

  // ponytail: hardcoded to match config.Config's APP_TIMEZONE default
  // (Asia/Kolkata) — there's no endpoint exposing the configured zone name
  // to the frontend. If APP_TIMEZONE is ever changed server-side, update
  // this to match.
  const DISPLAY_TZ = $derived(session.me?.business_timezone || 'Asia/Kolkata');

  const queryClient = useQueryClient();

  let days = $state(90);
  const WINDOWS = [30, 90, 365] as const;

  const analyticsQuery = createQuery(() => ({
    queryKey: ['hr-employee-analytics', userId, days],
    queryFn: () => hrAnalyticsApi.get(userId, days)
  }));
  // Flags (GET /api/hr/users/:id/flags) — the same engine behind Overview >
  // Flags, scoped to this person and driven by the same window chips. The
  // page's 7d equivalent doesn't exist here on purpose: a week is below most
  // of the detectors' sample gates, so the chip would mostly render "not
  // evaluated" and teach HR the section is broken.
  const flagRange = $derived<HRInsightRangeKey>(days === 30 ? '30d' : days === 90 ? '90d' : '365d');
  const flagsQuery = createQuery(() => ({
    queryKey: ['hr-employee-flags', userId, flagRange],
    queryFn: () => hrUserFlagsApi.get(userId, flagRange)
  }));

  // A flag renders through the same card the insights feed uses — same
  // severity vocabulary, same render/graph contract — so this is a shape
  // adaptation, not a second renderer. `evidence` is the card's summary;
  // `basis` is printed under the card, never folded into it.
  function flagAsCard(f: HRFlag): HRInsightCard {
    return {
      id: f.id,
      type: f.type.replace(/_/g, ' '),
      title: f.title,
      summary: f.evidence,
      severity: f.severity,
      scope: { type: 'user', id: f.user_id, label: f.user_name },
      time_range: f.time_range,
      metrics: f.metrics,
      render: f.render,
      graph: f.graph
    };
  }

  const profileQuery = createQuery(() => ({
    queryKey: ['hr-employee-profile', userId],
    queryFn: () => hrProfileApi.get(userId)
  }));
  const leaveQuery = createQuery(() => ({
    queryKey: ['hr-employee-leave', userId],
    queryFn: () => hrLeaveApi.userLeave(userId)
  }));
  const auditQuery = createQuery(() => ({
    queryKey: ['hr-employee-audit', userId],
    queryFn: () => hrAuditApi.userAudit(userId)
  }));
  const workZonesQuery = createQuery(() => ({
    queryKey: ['hr-work-zones'],
    queryFn: () => hrWorkZonesApi.list(),
    enabled: session.isHR || session.isAdmin
  }));
  // Devices backs the Devices section + the Reset device action. There is no
  // per-user device endpoint — GET /api/hr/devices returns every user's row,
  // and the whole directory shares this cache entry with Sites > Devices.
  const devicesQuery = createQuery(() => ({
    queryKey: ['hr-devices'],
    queryFn: () => hrDevicesApi.list()
  }));
  const device = $derived((devicesQuery.data ?? []).find((d) => d.user_id === userId) ?? null);
  // Admin-only (GET /api/admin/users/:id/board 403s for hr) — gated on
  // session.isAdmin so hr never even fires the request, and the section
  // below only renders once real data comes back.
  const boardQuery = createQuery(() => ({
    queryKey: ['hr-employee-board', userId],
    queryFn: () => adminBoardApi.get(userId),
    enabled: session.isAdmin
  }));
  const BOARD_COLUMNS: { id: string; label: string }[] = [
    { id: 'todo', label: 'To Do' },
    { id: 'doing', label: 'Doing' },
    { id: 'blocked', label: 'Blocked' },
    { id: 'done', label: 'Done' }
  ];

  function refreshAttendance() {
    queryClient.invalidateQueries({ queryKey: ['hr-employee-analytics', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-employee-audit', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-overview'] });
  }
  function refreshProfile() {
    queryClient.invalidateQueries({ queryKey: ['hr-employee-profile', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-employee-audit', userId] });
  }
  function refreshLeave() {
    queryClient.invalidateQueries({ queryKey: ['hr-employee-leave', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-employee-audit', userId] });
  }
  function refreshAccount() {
    queryClient.invalidateQueries({ queryKey: ['hr-devices'] });
    queryClient.invalidateQueries({ queryKey: ['hr-users'] });
    queryClient.invalidateQueries({ queryKey: ['hr-employee-analytics', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-employee-audit', userId] });
  }

  function fmtDateTime(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
      timeZone: DISPLAY_TZ,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function fmtDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric' });
  }

  function label(key: string): string {
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  }

  // Request statuses come off the API as raw strings; anything unrecognised
  // falls back to the neutral badge rather than rendering unstyled.
  function badgeKind(status: string): BadgeKind {
    if (status === 'approved' || status === 'rejected' || status === 'pending' || status === 'logged') return status;
    return 'info';
  }

  // Per-row map toggles (📍 button): expanding a work-log or failed-attempt
  // row reveals an inline Leaflet map (see leafletMap's setView-before-
  // getBounds fix). Keyed by index within each list's own render.
  const expandedLogs = new SvelteSet<number>();
  const expandedAttempts = new SvelteSet<number>();
  function toggle(set: SvelteSet<number>, i: number) {
    if (set.has(i)) set.delete(i);
    else set.add(i);
  }

  const PUNCH_IN_COLOR = '#1d4ed8';
  const PUNCH_OUT_COLOR = '#c8412b';
  const ZONE_COLOR = '#16a34a';

  function workLogMapParams(wl: HRAnalyticsRecentWorkLog): LeafletMapParams {
    const points: MapPoint[] = [];
    if (wl.punch_in_lat != null && wl.punch_in_lng != null) {
      points.push({ lat: wl.punch_in_lat, lng: wl.punch_in_lng, color: PUNCH_IN_COLOR, tooltip: 'Punch in', accuracyM: wl.punch_in_accuracy_m });
    }
    if (wl.punch_out_lat != null && wl.punch_out_lng != null) {
      points.push({ lat: wl.punch_out_lat, lng: wl.punch_out_lng, color: PUNCH_OUT_COLOR, tooltip: 'Punch out', accuracyM: wl.punch_out_accuracy_m });
    }
    const circles: MapCircle[] = [];
    if (wl.work_zone_lat != null && wl.work_zone_lng != null && wl.work_zone_radius != null) {
      circles.push({ lat: wl.work_zone_lat, lng: wl.work_zone_lng, radiusM: wl.work_zone_radius, color: ZONE_COLOR, tooltip: wl.work_zone_name });
    }
    return { points, circles };
  }

  function attemptMapParams(a: HRAnalyticsFailedAttempt): LeafletMapParams {
    const points: MapPoint[] = [{ lat: a.lat, lng: a.lng, color: PUNCH_OUT_COLOR, tooltip: 'Attempt', accuracyM: a.accuracy_m }];
    const circles: MapCircle[] = [];
    if (a.zone_lat != null && a.zone_lng != null && a.zone_radius != null) {
      circles.push({ lat: a.zone_lat, lng: a.zone_lng, radiusM: a.zone_radius, color: ZONE_COLOR, tooltip: a.zone_name });
    }
    return { points, circles };
  }

  const ROLE_CLASS: Record<string, string> = {
    employee: 'tym-badge--role-employee',
    hr: 'tym-badge--role-hr',
    admin: 'tym-badge--role-admin'
  };

  const targetRole = $derived(analyticsQuery.data?.profile.role ?? 'employee');

  // canAct mirrors the server-side privilege matrix (see
  // internal/api/handlers/hr_users.go): hr may act on employees only, admin
  // may act on anyone. Hiding the buttons is purely so nobody is offered an
  // action that would come back a 403 — the API is the actual gate.
  const canAct = $derived(session.isAdmin || (session.isHR && targetRole === 'employee'));
  const isSelf = $derived(session.me?.id === userId);

  const initials = $derived(
    (analyticsQuery.data?.profile.name || analyticsQuery.data?.profile.email || '?')
      .trim()
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
  );

  // Anchor rail: scrolls to a section, never hides one. Continuity is
  // admin-only, so its anchor is too.
  const SECTIONS = $derived(
    [
      { id: 'person-flags', label: 'Flags' },
      { id: 'person-attendance', label: 'Attendance' },
      { id: 'person-leave', label: 'Leave' },
      { id: 'person-requests', label: 'Requests' },
      { id: 'person-profile', label: 'Profile' },
      { id: 'person-account', label: 'Devices & access' },
      ...(session.isAdmin ? [{ id: 'person-board', label: 'Board' }] : []),
      { id: 'person-history', label: 'History' },
      ...(session.isAdmin ? [{ id: 'person-continuity', label: 'Continuity' }] : [])
    ].filter(Boolean)
  );

  function jumpTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ============================================================
  // PROFILE — HR-editable inline forms (contact/emergency, employment) plus
  // an admin-only card (government IDs, compensation, documents) that only
  // ever renders when GET .../profile actually returned an `admin` block
  // (see EmployeeProfile.admin). Forms are seeded from the query result
  // once (profileLoaded) rather than on every refetch, so an in-progress
  // edit in one section survives a background refetch triggered by saving
  // another section.
  // ============================================================
  let profileLoaded = $state(false);
  let contactForm = $state({
    phone: '',
    personal_email: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });
  let employmentForm = $state({
    designation: '',
    department: '',
    gender: '' as Gender,
    date_of_joining: '',
    employment_type: '' as EmploymentType,
    work_location: ''
  });
  let adminForm = $state({ aadhaar_number: '', pan_number: '', annual_ctc_amount: '', compensation_note: '' });

  $effect(() => {
    const p = profileQuery.data;
    if (!p || profileLoaded) return;
    contactForm = {
      phone: p.phone,
      personal_email: p.personal_email,
      address: p.address,
      emergency_contact_name: p.emergency_contact_name,
      emergency_contact_phone: p.emergency_contact_phone
    };
    employmentForm = {
      designation: p.designation,
      department: p.department,
      gender: p.gender,
      date_of_joining: p.date_of_joining ? p.date_of_joining.slice(0, 10) : '',
      employment_type: p.employment_type,
      work_location: p.work_location
    };
    if (p.admin) {
      adminForm = {
        aadhaar_number: p.admin.aadhaar_number,
        pan_number: p.admin.pan_number,
        annual_ctc_amount: p.admin.annual_ctc_amount != null ? String(p.admin.annual_ctc_amount) : '',
        compensation_note: p.admin.compensation_note
      };
    }
    profileLoaded = true;
  });

  let savingContact = $state(false);
  let savingEmployment = $state(false);
  let savingAdmin = $state(false);

  async function saveContact() {
    savingContact = true;
    try {
      await hrProfileApi.update(userId, contactForm);
      refreshProfile();
      toast.success('Contact info saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingContact = false;
    }
  }

  async function saveEmployment() {
    savingEmployment = true;
    try {
      const body: EmployeeProfileUpdate = { ...employmentForm };
      body.date_of_joining = employmentForm.date_of_joining ? new Date(employmentForm.date_of_joining).toISOString() : null;
      await hrProfileApi.update(userId, body);
      refreshProfile();
      toast.success('Employment info saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingEmployment = false;
    }
  }

  async function saveAdmin() {
    savingAdmin = true;
    try {
      await hrProfileApi.update(userId, {
        aadhaar_number: adminForm.aadhaar_number,
        pan_number: adminForm.pan_number,
        annual_ctc_amount: adminForm.annual_ctc_amount === '' ? null : Number(adminForm.annual_ctc_amount),
        compensation_note: adminForm.compensation_note
      });
      refreshProfile();
      toast.success('Government ID / compensation saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingAdmin = false;
    }
  }

  let uploadLabel = $state('');
  let uploadFile = $state<File | null>(null);
  let uploading = $state(false);

  let fileInputEl = $state<HTMLInputElement | null>(null);

  function onFileChange(e: Event) {
    uploadFile = (e.target as HTMLInputElement).files?.[0] ?? null;
  }

  async function uploadDocument() {
    if (!uploadFile) return;
    uploading = true;
    try {
      await hrProfileApi.uploadDocument(userId, uploadFile, uploadLabel);
      refreshProfile();
      uploadLabel = '';
      uploadFile = null;
      if (fileInputEl) fileInputEl.value = '';
      toast.success('Document uploaded.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      uploading = false;
    }
  }

  async function downloadDocument(doc: EmployeeDocument) {
    try {
      await hrProfileApi.downloadDocument(userId, doc);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function deleteDocument(docId: number, filename: string) {
    if (!confirm(`Delete "${filename}"?`)) return;
    try {
      await hrProfileApi.deleteDocument(userId, docId);
      refreshProfile();
      toast.success('Document deleted.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  // ============================================================
  // ACCOUNT ACTIONS — reset password / device / MFA. All three are gated by
  // canAct above and by the server's own privilege matrix. Password reset
  // takes a value, so it's a modal; the other two are two-step inline
  // confirms (same pattern as deleteCustody — a native confirm() freezes the
  // renderer under CDP/browser automation).
  // ============================================================
  let showResetPassword = $state(false);
  let newPassword = $state('');
  let resetPasswordError = $state('');
  let resettingPassword = $state(false);

  function openResetPassword() {
    newPassword = '';
    resetPasswordError = '';
    showResetPassword = true;
  }

  async function submitResetPassword() {
    if (newPassword.length < 8) {
      resetPasswordError = 'Password must be at least 8 characters.';
      return;
    }
    resettingPassword = true;
    resetPasswordError = '';
    try {
      await hrUsersApi.resetPassword(userId, newPassword);
      showResetPassword = false;
      refreshAccount();
      toast.success('Password reset. Share it with them over a private channel.');
    } catch (err) {
      resetPasswordError = getErrorMessage(err);
    } finally {
      resettingPassword = false;
    }
  }

  let confirmingResetDevice = $state(false);
  let confirmResetDeviceTimer: ReturnType<typeof setTimeout> | undefined;
  let resettingDevice = $state(false);

  async function resetDevice() {
    if (!confirmingResetDevice) {
      confirmingResetDevice = true;
      clearTimeout(confirmResetDeviceTimer);
      confirmResetDeviceTimer = setTimeout(() => (confirmingResetDevice = false), 4000);
      return;
    }
    confirmingResetDevice = false;
    clearTimeout(confirmResetDeviceTimer);
    resettingDevice = true;
    try {
      await hrDevicesApi.reset(userId);
      refreshAccount();
      toast.success('Device unbound — they can register a new one at next login.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      resettingDevice = false;
    }
  }

  let confirmingResetMFA = $state(false);
  let confirmResetMFATimer: ReturnType<typeof setTimeout> | undefined;
  let resettingMFA = $state(false);

  async function resetMFA() {
    if (!confirmingResetMFA) {
      confirmingResetMFA = true;
      clearTimeout(confirmResetMFATimer);
      confirmResetMFATimer = setTimeout(() => (confirmingResetMFA = false), 4000);
      return;
    }
    confirmingResetMFA = false;
    clearTimeout(confirmResetMFATimer);
    resettingMFA = true;
    try {
      await hrUsersApi.resetMFA(userId);
      refreshAccount();
      toast.success('Two-factor reset — they will re-enrol at next login.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      resettingMFA = false;
    }
  }

  // ============================================================
  // ATTENDANCE extras — forgot-punch-out Fix dialog + Reset warnings.
  // ============================================================
  let fixingLog = $state<HRAnalyticsRecentWorkLog | null>(null);
  let fixPunchOut = $state('');
  let fixError = $state('');
  let fixing = $state(false);

  function openFix(wl: HRAnalyticsRecentWorkLog) {
    fixingLog = wl;
    // datetime-local wants "YYYY-MM-DDTHH:mm" in local time — default to the
    // punch-in time plus 8 hours as a starting guess HR can correct.
    const guess = new Date(new Date(wl.punch_in).getTime() + 8 * 3_600_000);
    const pad = (n: number) => String(n).padStart(2, '0');
    fixPunchOut = `${guess.getFullYear()}-${pad(guess.getMonth() + 1)}-${pad(guess.getDate())}T${pad(guess.getHours())}:${pad(guess.getMinutes())}`;
    fixError = '';
  }

  async function submitFix() {
    if (!fixingLog || !fixPunchOut) return;
    fixing = true;
    fixError = '';
    try {
      await hrAttendanceApi.fixWorkLog(fixingLog.id, new Date(fixPunchOut).toISOString());
      refreshAttendance();
      fixingLog = null;
      toast.success('Punch-out fixed.');
    } catch (err) {
      fixError = getErrorMessage(err);
    } finally {
      fixing = false;
    }
  }

  let resettingWarnings = $state(false);
  async function resetWarnings() {
    if (!confirm('Reset the forgot-punch-out warning counter? Only occurrences from now on will count.')) return;
    resettingWarnings = true;
    try {
      await hrAttendanceApi.resetWarnings(userId);
      refreshAttendance();
      toast.success('Warning counter reset.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      resettingWarnings = false;
    }
  }

  let showManualAttendance = $state(false);
  let manualPunchIn = $state('');
  let manualPunchOut = $state('');
  let manualReason = $state('');
  let manualWorkZoneID = $state('');
  let manualAttendanceError = $state('');
  let savingManualAttendance = $state(false);

  function openManualAttendance() {
    manualPunchIn = businessWallClock(new Date(), DISPLAY_TZ);
    manualPunchOut = '';
    manualReason = '';
    manualWorkZoneID = '';
    manualAttendanceError = '';
    showManualAttendance = true;
  }

  async function submitManualAttendance() {
    if (savingManualAttendance) return;
    if (!manualPunchIn || !manualReason.trim()) {
      manualAttendanceError = 'Start time and reason are required.';
      return;
    }
    savingManualAttendance = true;
    manualAttendanceError = '';
    try {
      const body: { punch_in: string; punch_out?: string; reason: string; work_zone_id?: number } = {
        punch_in: businessWallClockToRFC3339(manualPunchIn, DISPLAY_TZ),
        reason: manualReason.trim()
      };
      if (manualPunchOut) body.punch_out = businessWallClockToRFC3339(manualPunchOut, DISPLAY_TZ);
      if (manualWorkZoneID) body.work_zone_id = Number(manualWorkZoneID);
      await hrAttendanceApi.markPresent(userId, body);
      showManualAttendance = false;
      refreshAttendance();
      toast.success('Manual attendance recorded.');
    } catch (err) {
      manualAttendanceError = getErrorMessage(err);
    } finally {
      savingManualAttendance = false;
    }
  }

  // ============================================================
  // LEAVE — quota vs availed for the current FY, recent ledger, adjust.
  // ============================================================
  let showAdjust = $state(false);
  let adjustLeaveTypeId = $state<number | null>(null);
  let adjustDays = $state('');
  let adjustNote = $state('');
  let adjustError = $state('');
  let adjusting = $state(false);
  let showOpeningBalance = $state(false);
  let openingLeaveTypeId = $state<number | null>(null);
  let openingDays = $state('');
  let openingNote = $state('');
  let openingError = $state('');
  let savingOpening = $state(false);
  const openingBalanceTypeIds = $derived(new Set((leaveQuery.data?.opening_balances ?? []).map((entry) => entry.leave_type_id)));
  const availableOpeningTypes = $derived(
    (leaveQuery.data?.types ?? []).filter((type) => !openingBalanceTypeIds.has(type.leave_type_id))
  );

  function leaveTypeName(leaveTypeId: number): string {
    return leaveQuery.data?.types.find((type) => type.leave_type_id === leaveTypeId)?.name ?? `Leave type ${leaveTypeId}`;
  }

  function openOpeningBalance() {
    openingLeaveTypeId = availableOpeningTypes[0]?.leave_type_id ?? null;
    openingDays = '';
    openingNote = '';
    openingError = '';
    showOpeningBalance = true;
  }

  async function submitOpeningBalance() {
    const days = Number(openingDays);
    if (openingLeaveTypeId == null || !openingDays || Number.isNaN(days) || days <= 0) {
      openingError = 'Choose a type and enter a positive number of days.';
      return;
    }
    savingOpening = true;
    openingError = '';
    try {
      await hrLeaveApi.backfill({
        user_id: userId,
        leave_type_id: openingLeaveTypeId,
        days,
        note: openingNote.trim()
      });
      refreshLeave();
      showOpeningBalance = false;
      toast.success('Opening balance saved.');
    } catch (err) {
      openingError = getErrorMessage(err);
    } finally {
      savingOpening = false;
    }
  }

  function openAdjust() {
    const types = leaveQuery.data?.types ?? [];
    adjustLeaveTypeId = types[0]?.leave_type_id ?? null;
    adjustDays = '';
    adjustNote = '';
    adjustError = '';
    showAdjust = true;
  }

  async function submitAdjust() {
    const days = Number(adjustDays);
    if (adjustLeaveTypeId == null || !adjustDays || Number.isNaN(days) || days === 0) {
      adjustError = 'Choose a type and a non-zero number of days.';
      return;
    }
    if (!adjustNote.trim()) {
      adjustError = 'A note is required.';
      return;
    }
    adjusting = true;
    adjustError = '';
    try {
      await hrLeaveApi.adjust(userId, { leave_type_id: adjustLeaveTypeId, days, note: adjustNote.trim() });
      refreshLeave();
      showAdjust = false;
      toast.success('Leave adjusted.');
    } catch (err) {
      adjustError = getErrorMessage(err);
    } finally {
      adjusting = false;
    }
  }

  // ============================================================
  // REQUESTS — HR records an attendance fact for this person and may correct
  // the dates on an existing request. The server owns approval, privilege,
  // overlap validation, and leave-ledger replacement.
  // ============================================================
  type ManagedRequestType = Exclude<HRRequestType, 'other'>;
  let requestForm = $state({
    type: 'leave' as ManagedRequestType,
    start_date: todayISO(),
    end_date: todayISO(),
    half_day: false,
    leave_type_id: null as number | null,
    reason: ''
  });
  let recordingRequest = $state(false);
  let requestError = $state('');

  $effect(() => {
    if (requestForm.leave_type_id == null && leaveQuery.data?.types[0]) {
      requestForm.leave_type_id = leaveQuery.data.types[0].leave_type_id;
    }
  });

  function refreshRequests() {
    queryClient.invalidateQueries({ queryKey: ['hr-employee-analytics', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-employee-leave', userId] });
    queryClient.invalidateQueries({ queryKey: ['hr-overview'] });
  }

  async function recordRequest() {
    if (!requestForm.start_date || !requestForm.reason.trim()) {
      requestError = 'Start date and reason are required.';
      return;
    }
    if (requestForm.type === 'leave' && requestForm.leave_type_id == null) {
      requestError = 'Choose a leave type.';
      return;
    }
    recordingRequest = true;
    requestError = '';
    const body: HRManagedRequestBody = {
      type: requestForm.type,
      start_date: requestForm.start_date,
      reason: requestForm.reason.trim()
    };
    if (requestForm.type === 'leave') {
      body.end_date = requestForm.half_day ? requestForm.start_date : requestForm.end_date;
      body.half_day = requestForm.half_day;
      body.reason_category = 'other';
      body.leave_type_id = requestForm.leave_type_id!;
    }
    try {
      await hrInboxApi.createForUser(userId, body);
      requestForm = { ...requestForm, start_date: todayISO(), end_date: todayISO(), half_day: false, reason: '' };
      refreshRequests();
      toast.success('Attendance request recorded and approved.');
    } catch (err) {
      requestError = getErrorMessage(err);
    } finally {
      recordingRequest = false;
    }
  }

  let editingRequest = $state<HRAnalyticsRecentRequest | null>(null);
  let editRequestStart = $state('');
  let editRequestEnd = $state('');
  let editingRequestSaving = $state(false);
  let editRequestError = $state('');

  function openRequestDateEdit(r: HRAnalyticsRecentRequest) {
    editingRequest = r;
    editRequestStart = r.start_date.slice(0, 10);
    editRequestEnd = (r.end_date || r.start_date).slice(0, 10);
    editRequestError = '';
    requestAnimationFrame(() => document.getElementById('request-date-editor')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  async function saveRequestDates() {
    const r = editingRequest;
    if (!r || !editRequestStart || r.type === 'other') return;
    editingRequestSaving = true;
    editRequestError = '';
    const body: HRManagedRequestBody = {
      type: r.type,
      start_date: editRequestStart,
      reason: r.reason,
      reason_category: r.reason_category ?? 'other'
    };
    if (r.type === 'leave') {
      body.end_date = r.half_day ? editRequestStart : editRequestEnd;
      body.half_day = r.half_day;
      if (r.leave_type_id != null) body.leave_type_id = r.leave_type_id;
    }
    try {
      await hrInboxApi.update(r.id, body);
      editingRequest = null;
      refreshRequests();
      toast.success('Request dates updated.');
    } catch (err) {
      editRequestError = getErrorMessage(err);
    } finally {
      editingRequestSaving = false;
    }
  }

  // ============================================================
  // HISTORY — read-only audit trail. HRUserAudit returns the full list (no
  // server-side pagination), so "Load more" just reveals more of the
  // already-fetched array rather than making another request.
  // ============================================================
  let historyVisible = $state(15);

  // ============================================================
  // CONTINUITY — admin only. Query itself never fires for hr (enabled
  // below), and none of this section renders for hr (session.isAdmin gate
  // in the template). Merged in from the old continuity/PersonDetail.svelte.
  // ============================================================
  const LAMP_TONE: Record<OKRColour, 'bad' | 'warn' | 'good'> = { red: 'bad', amber: 'warn', green: 'good' };

  const PAY_BAND_OPTIONS: { value: PayBandPosition; label: string }[] = [
    { value: '', label: 'Not set' },
    { value: 'below', label: 'Below' },
    { value: 'in', label: 'In' },
    { value: 'above', label: 'Above' }
  ];
  const CONVERSATION_TYPE_OPTIONS: { value: ConversationType; label: string }[] = [
    { value: '3_month', label: '3 month' },
    { value: '6_month', label: '6 month' },
    { value: '12_month', label: '12 month' },
    { value: 'regular', label: 'Regular' },
    { value: 'exit', label: 'Exit' }
  ];

  const CONTINUITY_TOOLTIP = {
    talkToThemBy:
      'Every 3 months for the first year, every 6 months after that. Do not hold it in the same month as a pay decision — the answers stop being honest.',
    payBandPosition: 'Below, in, or above the market range for this role. We do not store salary figures here.',
    custody: 'Accounts, admin rights, and relationships that would be difficult to recover if this person left tomorrow.'
  };

  const CUSTODY_TYPE_LABEL: Record<CustodyType, string> = {
    account: 'Account',
    admin_right: 'Admin right',
    signatory: 'Signatory',
    vendor: 'Vendor relationship',
    customer: 'Customer relationship',
    dealer: 'Dealer relationship'
  };
  const CUSTODY_TYPE_OPTIONS: { value: CustodyType; label: string }[] = (
    Object.entries(CUSTODY_TYPE_LABEL) as [CustodyType, string][]
  ).map(([value, label]) => ({ value, label }));

  const personQuery = createQuery(() => ({
    queryKey: ['continuity-person', userId],
    queryFn: () => peopleApi.get(userId),
    enabled: session.isAdmin
  }));
  // hrUsersApi.list() backs the conversation/action/custody "who" pickers
  // below — only ever rendered for admin, so only ever fetched for admin.
  const continuityUsersQuery = createQuery(() => ({
    queryKey: ['hr-users'],
    queryFn: () => hrUsersApi.list(),
    enabled: session.isAdmin
  }));

  function refreshContinuity() {
    queryClient.invalidateQueries({ queryKey: ['continuity-person', userId] });
    queryClient.invalidateQueries({ queryKey: ['continuity-people'] });
  }

  function okrRefClick(ref: OKRRef) {
    onJumpToOKR(ref.id);
  }

  // ---- Details (department / date of joining / pay dates / pay band) ----
  let continuityForm = $state({
    department: '',
    date_of_joining: '',
    last_increment_date: '',
    last_role_change_date: '',
    pay_band_position: '' as PayBandPosition
  });
  let continuityFormDirty = $state(false);
  let savingContinuityDetails = $state(false);

  $effect(() => {
    const p = personQuery.data;
    if (p && !continuityFormDirty) {
      continuityForm = {
        department: p.group,
        date_of_joining: p.date_of_joining || '',
        last_increment_date: p.last_increment_date || '',
        last_role_change_date: p.last_role_change_date || '',
        pay_band_position: p.pay_band_position
      };
    }
  });

  async function saveContinuityDetails() {
    savingContinuityDetails = true;
    try {
      await peopleApi.update(userId, {
        department: continuityForm.department,
        date_of_joining: continuityForm.date_of_joining || undefined,
        last_increment_date: continuityForm.last_increment_date || undefined,
        last_role_change_date: continuityForm.last_role_change_date || undefined,
        pay_band_position: continuityForm.pay_band_position
      });
      continuityFormDirty = false;
      refreshContinuity();
      toast.success('Saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingContinuityDetails = false;
    }
  }

  // ---- Conversations ----
  let showConversation = $state(false);
  let conversationForm = $state({ date: '', type: 'regular' as ConversationType, conducted_by: 0, notes: '' });
  let conversationError = $state('');
  let savingConversation = $state(false);

  function openConversation() {
    conversationForm = { date: new Date().toISOString().slice(0, 10), type: 'regular', conducted_by: 0, notes: '' };
    conversationError = '';
    showConversation = true;
  }

  async function submitConversation() {
    if (!conversationForm.date || !conversationForm.conducted_by) {
      conversationError = 'Date and who conducted it are required.';
      return;
    }
    savingConversation = true;
    conversationError = '';
    try {
      const updated = await peopleApi.logConversation(userId, conversationForm);
      showConversation = false;
      refreshContinuity();
      toast.success(`Conversation logged. Talk to them by: ${updated.talk_to_them_by || '—'}.`);
    } catch (err) {
      conversationError = getErrorMessage(err);
    } finally {
      savingConversation = false;
    }
  }

  // ---- Actions ----
  let showActionModal = $state(false);
  let editingAction = $state<ContinuityActionRow | null>(null);
  let actionForm = $state({ description: '', owner_id: 0, due_date: '', status: 'open' as 'open' | 'done' });
  let actionError = $state('');
  let savingAction = $state(false);

  function openNewAction() {
    editingAction = null;
    actionForm = { description: '', owner_id: 0, due_date: '', status: 'open' };
    actionError = '';
    showActionModal = true;
  }

  function openEditAction(a: ContinuityActionRow) {
    editingAction = a;
    actionForm = { description: a.description, owner_id: a.owner_id, due_date: a.due_date || '', status: a.status };
    actionError = '';
    showActionModal = true;
  }

  async function submitAction() {
    if (!actionForm.description.trim() || !actionForm.owner_id) {
      actionError = 'Description and owner are required.';
      return;
    }
    savingAction = true;
    actionError = '';
    try {
      if (editingAction) {
        await okrsApi.updateAction(editingAction.id, actionForm);
      } else {
        await peopleApi.createAction(userId, actionForm);
      }
      showActionModal = false;
      refreshContinuity();
      toast.success('Action saved.');
    } catch (err) {
      actionError = getErrorMessage(err);
    } finally {
      savingAction = false;
    }
  }

  async function toggleActionDone(a: ContinuityActionRow) {
    try {
      await okrsApi.updateAction(a.id, { status: a.status === 'done' ? 'open' : 'done' });
      refreshContinuity();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  // ---- Custody ("What they hold") ----
  let showCustodyModal = $state(false);
  let editingCustody = $state<CustodyRow | null>(null);
  let custodyForm = $state({ type: 'account' as CustodyType, name: '', second_holder_id: 0, last_verified: '' });
  let custodyError = $state('');
  let savingCustody = $state(false);

  function openNewCustody() {
    editingCustody = null;
    custodyForm = { type: 'account', name: '', second_holder_id: 0, last_verified: '' };
    custodyError = '';
    showCustodyModal = true;
  }

  function openEditCustody(cu: CustodyRow) {
    editingCustody = cu;
    custodyForm = { type: cu.type, name: cu.name, second_holder_id: cu.second_holder_id || 0, last_verified: cu.last_verified || '' };
    custodyError = '';
    showCustodyModal = true;
  }

  async function submitCustody() {
    if (!custodyForm.name.trim()) {
      custodyError = 'Name is required.';
      return;
    }
    savingCustody = true;
    custodyError = '';
    try {
      if (editingCustody) {
        await custodyApi.update(editingCustody.id, custodyForm);
      } else {
        await custodyApi.create({ ...custodyForm, holder_id: userId });
      }
      showCustodyModal = false;
      refreshContinuity();
      toast.success('Saved.');
    } catch (err) {
      custodyError = getErrorMessage(err);
    } finally {
      savingCustody = false;
    }
  }

  // Two-step inline confirm instead of native confirm() — a native dialog
  // freezes the renderer for CDP/browser automation. First click arms the
  // row (auto-reverts after 4s or on blur); second click while armed
  // actually deletes. Tracks the armed row's id since each custody row has
  // its own delete button.
  let confirmingDeleteCustodyId = $state<number | null>(null);
  let confirmDeleteCustodyTimer: ReturnType<typeof setTimeout> | undefined;

  async function deleteCustody(cu: CustodyRow) {
    if (confirmingDeleteCustodyId !== cu.id) {
      confirmingDeleteCustodyId = cu.id;
      clearTimeout(confirmDeleteCustodyTimer);
      confirmDeleteCustodyTimer = setTimeout(() => (confirmingDeleteCustodyId = null), 4000);
      return;
    }
    confirmingDeleteCustodyId = null;
    clearTimeout(confirmDeleteCustodyTimer);
    try {
      await custodyApi.remove(cu.id);
      refreshContinuity();
      toast.success('Removed.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  // ---- "Someone is leaving" (notice) ----
  let showNoticeModal = $state(false);
  let noticeLastWorkingDay = $state('');
  let noticeError = $state('');
  let savingNotice = $state(false);

  function openNoticeModal() {
    noticeLastWorkingDay = '';
    noticeError = '';
    showNoticeModal = true;
  }

  async function submitNotice() {
    if (!noticeLastWorkingDay) {
      noticeError = 'Last working day is required.';
      return;
    }
    savingNotice = true;
    noticeError = '';
    try {
      await peopleApi.setNotice(userId, noticeLastWorkingDay);
      showNoticeModal = false;
      refreshContinuity();
      toast.success('Serving notice — handover checklist created.');
    } catch (err) {
      noticeError = getErrorMessage(err);
    } finally {
      savingNotice = false;
    }
  }

  // Same two-step inline confirm as deleteCustody above.
  let confirmingCancelNotice = $state(false);
  let confirmCancelNoticeTimer: ReturnType<typeof setTimeout> | undefined;

  async function cancelNotice() {
    if (!confirmingCancelNotice) {
      confirmingCancelNotice = true;
      clearTimeout(confirmCancelNoticeTimer);
      confirmCancelNoticeTimer = setTimeout(() => (confirmingCancelNotice = false), 4000);
      return;
    }
    confirmingCancelNotice = false;
    clearTimeout(confirmCancelNoticeTimer);
    try {
      await peopleApi.cancelNotice(userId);
      refreshContinuity();
      toast.success('Notice cancelled.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  function daysRemaining(lastWorkingDay: string): number {
    const ms = new Date(lastWorkingDay + 'T00:00:00').getTime() - new Date(new Date().toDateString()).getTime();
    return Math.round(ms / (24 * 60 * 60 * 1000));
  }

  async function copyPersonDetails(name: string, email: string) {
    const ok = await copyText(formatPersonDetails({ id: userId, name, email }));
    if (ok) toast.success('Copied name, email, and user ID.');
    else toast.error('Could not copy details.');
  }
</script>

<div class="tym-page {canAct ? 'tym-dock-gap' : ''}">
  {#if analyticsQuery.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if analyticsQuery.isError}
    <div class="tym-error-state">Could not load this employee's analytics.</div>
  {:else if analyticsQuery.data}
    {@const d = analyticsQuery.data}

    <!-- ============================================================
         IDENTITY HEADER — who this is, at a glance.
         ============================================================ -->
    <header class="mb-4 overflow-hidden rounded-2xl bg-grad-hero" style="box-shadow:var(--tym-shadow-2)">
      <div class="flex flex-wrap items-start gap-4 p-5">
        <span
          class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full font-heading text-lg font-bold text-brand-navy"
          style="background:linear-gradient(135deg,#226A73,#97D700)"
          aria-hidden="true"
        >
          {initials}
        </span>
        <div class="min-w-0 flex-1">
          <h1 class="truncate font-heading text-[24px] font-bold leading-tight tracking-[-.02em] text-white">
            {d.profile.name || d.profile.email}
          </h1>
          <p class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-white/70">
            {#if profileQuery.data?.designation}
              <span>{profileQuery.data.designation}</span>
              <span class="text-white/30">·</span>
            {/if}
            <a class="inline-flex items-center gap-1 hover:text-white" href="mailto:{d.profile.email}">
              <Mail class="h-3.5 w-3.5" />{d.profile.email}
            </a>
            {#if profileQuery.data?.phone}
              <span class="text-white/30">·</span>
              <span class="inline-flex items-center gap-1"><Phone class="h-3.5 w-3.5" />{profileQuery.data.phone}</span>
            {/if}
            {#if d.profile.work_zone_names.length > 0}
              <span class="text-white/30">·</span>
              <span class="inline-flex items-center gap-1"
                ><Building2 class="h-3.5 w-3.5" />{d.profile.work_zone_names.join(', ')}</span
              >
            {/if}
          </p>
          <div class="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span class="tym-badge {ROLE_CLASS[d.profile.role]}">{d.profile.role}</span>
            {#if d.profile.active}
              <span class="tym-badge tym-badge--approved">Active</span>
            {:else}
              <span class="tym-badge tym-badge--rejected">Inactive</span>
            {/if}
            {#if d.profile.punch_mode === 'field'}<span class="tym-badge tym-badge--info">Hotel field technician</span>{/if}
            {#if d.profile.punch_mode === 'untagged'}<span class="tym-badge tym-badge--info">Sales</span>{/if}
            {#if d.profile.punch_mode === 'home'}<span class="tym-badge tym-badge--info">Home or office</span>{/if}
            {#if d.warning_count > 0}
              <span class="tym-badge tym-badge--pending">
                {d.warning_count} warning{d.warning_count === 1 ? '' : 's'}
              </span>
            {/if}
            {#if personQuery.data?.notice_status === 'serving_notice'}
              <span class="tym-badge tym-badge--alert">Serving notice</span>
            {/if}
            <button
              type="button"
              class="tym-badge cursor-pointer border border-white/25 bg-white/10 text-white hover:bg-white/20"
              onclick={() => copyPersonDetails(d.profile.name, d.profile.email)}
            >
              Copy details
            </button>
          </div>
        </div>
        <!-- Full width on a phone, so the window selector drops to its own
             line instead of squeezing the name into an ellipsis. -->
        <div class="flex w-full shrink-0 flex-col items-start gap-2 sm:w-auto sm:items-end">
          <div class="flex gap-1.5">
            {#each WINDOWS as w (w)}
              <button
                type="button"
                class="min-h-[30px] rounded-full border px-3 text-[12px] font-semibold transition-colors {days === w
                  ? 'border-toyama-lime bg-toyama-lime text-brand-navy'
                  : 'border-white/25 text-white/75 hover:bg-white/10'}"
                onclick={() => (days = w)}
              >
                {w}d
              </button>
            {/each}
          </div>
          {#if d.warning_count > 0 && canAct}
            <button type="button" class="btn-secondary !border-white/25 !bg-white/10 !text-white hover:!bg-white/20" disabled={resettingWarnings} onclick={resetWarnings}>
              <RotateCcw class="h-3.5 w-3.5" /> {resettingWarnings ? 'Resetting...' : 'Reset warnings'}
            </button>
          {/if}
        </div>
      </div>

      <!-- Headline numbers, on the header so the page opens with an answer. -->
      <div class="grid grid-cols-2 gap-px border-t border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-6">
        {#each [{ label: 'Avg in', value: d.attendance.avg_check_in ?? '—' }, { label: 'Avg out', value: d.attendance.avg_check_out ?? '—' }, { label: 'Avg hrs/day', value: `${d.attendance.avg_hours_per_day}h` }, { label: 'Days present', value: d.attendance.days_present }, { label: 'Leave days', value: d.leave.total_leave_days }, { label: 'Incomplete', value: d.attendance.incomplete_punches }] as stat (stat.label)}
          <div class="bg-brand-navy px-4 py-3">
            <p class="text-[10px] font-semibold uppercase tracking-[.12em] text-white/45">{stat.label}</p>
            <p class="mt-0.5 font-heading text-[19px] font-bold text-white">{stat.value}</p>
          </div>
        {/each}
      </div>
    </header>

    <!-- Anchor rail — scrolls to a section, never hides one. On a phone this
         page is several thousand pixels of record, so the rail is pinned and
         scrolls sideways rather than wrapping to three lines of chips. -->
    <FilterChipRow
      class="mb-5"
      chips={SECTIONS.map((s) => ({ id: s.id, label: s.label }))}
      onSelect={jumpTo}
      ariaLabel="Jump to section"
      sticky
    />

    <!-- ============================================================
         FLAGS — always rendered, empty state included, so HR can see the
         engine ran. A clean record is a result, not an absence of one.
         ============================================================ -->
    <section id="person-flags" class="mb-7 scroll-mt-4">
      <h2 class="tym-section-title mb-1 text-base">Flags</h2>
      <p class="tym-section-sub mb-3">
        Patterns in this person's punches and leave over the {days}-day window, measured against the office-hours
        baseline. Thresholds are configured defaults, not company policy — each card states the one it used.
      </p>

      {#if flagsQuery.isLoading}
        <div class="tym-loading-state">Loading...</div>
      {:else if flagsQuery.isError}
        <div class="tym-error-state">Could not load flags.</div>
      {:else if flagsQuery.data}
        {@const person = flagsQuery.data.person}
        {#if !person.evaluated}
          <div class="tym-card">
            <EmptyState
              title="Not enough of this window to evaluate"
              body={`${person.expected_days} working days fell inside this person's employment in the last ${days} days — below the sample gate, so nothing was measured either way.`}
            />
          </div>
        {:else if person.flags.length === 0}
          <div class="tym-card">
            <EmptyState
              title="Nothing to flag in this window."
              body={`${person.expected_days} working days evaluated against the ${flagsQuery.data.baseline.start} baseline. Nothing crossed a threshold.`}
            />
          </div>
        {:else}
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {#each person.flags as flag (flag.id)}
              <div>
                <AttendanceInsightCard card={flagAsCard(flag)} />
                <p class="mt-1.5 px-1 text-[11.5px] leading-relaxed text-dark-gray">{flag.basis}</p>
              </div>
            {/each}
          </div>
        {/if}
      {/if}
    </section>

    <!-- ============================================================
         ATTENDANCE
         ============================================================ -->
    <section id="person-attendance" class="mb-7 scroll-mt-4">
      <h2 class="tym-section-title mb-1 text-base">Attendance</h2>
      <div class="mb-3 flex flex-wrap items-end justify-between gap-2">
        <p class="tym-section-sub">Hours worked, where they punched from, and anything that needs fixing.</p>
        {#if canAct}
          <button type="button" class="btn-secondary" onclick={openManualAttendance}><Plus class="h-3.5 w-3.5" /> Mark present</button>
        {/if}
      </div>

      <div class="tym-card mb-4 p-5">
        <p class="mb-3 text-[13px] font-semibold text-navy">Daily hours ({d.days}d)</p>
        {#if d.daily.length === 0}
          <p class="text-[13px] text-dark-gray">No punches in this window.</p>
        {:else}
          {@const maxHours = Math.max(1, ...d.daily.map((e) => e.hours))}
          {@const w = 100 / d.daily.length}
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" class="h-28 w-full" role="img" aria-label="Daily hours worked">
            <line x1="0" y1="38" x2="100" y2="38" class="stroke-toyama-cloudy-gray" stroke-width="0.5" />
            {#each d.daily as entry, i (entry.date)}
              {@const h = (entry.hours / maxHours) * 36}
              <rect x={i * w + w * 0.15} y={38 - h} width={w * 0.7} height={h} class="fill-toyama-teal" rx="0.5">
                <title
                  >{entry.date}: {entry.hours}h{entry.first_in ? `, in at ${entry.first_in}` : ''}{entry.last_out
                    ? `, out at ${entry.last_out}`
                    : ''}</title
                >
              </rect>
            {/each}
          </svg>
          <div class="mt-1.5 flex justify-between text-[11px] text-dark-gray">
            <span>{d.daily[0].date}</span>
            <span>{d.daily[d.daily.length - 1].date}</span>
          </div>
        {/if}
      </div>

      <div class="mb-4">
        <p class="mb-2 text-[13px] font-semibold text-navy">Recent work logs</p>

        <!-- Phones get one card per punch instead of a six-column table:
             the same fields, stacked, with the map toggle and the Fix action
             as real touch targets rather than 24px icons in a cell you have
             to pan sideways to reach. The table below is unchanged from sm. -->
        <div class="sm:hidden">
          {#each d.recent.work_logs as wl, i (i)}
            <div class="tym-list-card">
              <div class="flex items-start gap-2">
                <div class="min-w-0 flex-1">
                  <p class="tym-list-card-title">
                    {fmtDateTime(wl.punch_in)}
                    <span class="text-dark-gray">&rarr;</span>
                    {wl.punch_out ? fmtDateTime(wl.punch_out) : '—'}
                  </p>
                   <p class="mt-1 text-[12px] text-dark-gray">
                     <span class="font-semibold text-navy">{wl.hours}h</span> &middot; {@render workLogWhere(wl)}
                   </p>
                  {#if wl.manual_attendance}<span class="tym-badge tym-badge--info mt-1">HR-entered</span>{/if}
                </div>
                {#if wl.punch_in_lat != null || wl.work_zone_lat != null}
                  <button
                    type="button"
                    class="btn-secondary tym-touch shrink-0 !px-3"
                    onclick={() => toggle(expandedLogs, i)}
                    aria-label="Toggle map"
                  >
                    <MapPin class="h-4 w-4" />
                  </button>
                {/if}
              </div>
              {#if wl.forgot_punch_out}
                <div class="mt-2 flex flex-wrap items-center gap-2">
                  {#if wl.forgot_fixed_at}
                    <span class="tym-badge tym-badge--approved">Fixed</span>
                  {:else}
                    <span class="tym-badge tym-badge--pending">Forgot punch-out</span>
                    {#if canAct}
                      <button type="button" class="btn-secondary tym-touch !px-3 !py-1.5 text-[12.5px]" onclick={() => openFix(wl)}>
                        <Wrench class="h-3.5 w-3.5" /> Fix
                      </button>
                    {/if}
                  {/if}
                </div>
              {/if}
              {#if expandedLogs.has(i)}
                <div
                  class="mt-2.5 h-[200px] w-full overflow-hidden rounded-md border border-toyama-cloudy-gray"
                  use:leafletMap={workLogMapParams(wl)}
                ></div>
              {/if}
            </div>
          {:else}
            <p class="tym-empty-state">No work logs yet.</p>
          {/each}
        </div>

        <div class="tym-table-wrap hidden sm:block">
          <div class="tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>In</th>
                  <th>Out</th>
                  <th>Hours</th>
                  <th>Where</th>
                  <th>Flags</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {#each d.recent.work_logs as wl, i (i)}
                  <tr>
                    <td class="muted">{fmtDateTime(wl.punch_in)}</td>
                    <td class="muted">{wl.punch_out ? fmtDateTime(wl.punch_out) : '—'}</td>
                    <td class="muted">{wl.hours}</td>
                    <td>{@render workLogWhere(wl)}</td>
                    <td>
                      {#if wl.manual_attendance}<span class="tym-badge tym-badge--info">HR-entered</span>{/if}
                      {#if wl.forgot_punch_out}
                        {#if wl.forgot_fixed_at}
                          <span class="tym-badge tym-badge--approved">Fixed</span>
                        {:else}
                          <span class="tym-badge tym-badge--pending">Forgot punch-out</span>
                          {#if canAct}
                            <button type="button" class="btn-secondary !py-1 !px-2 text-[12px]" onclick={() => openFix(wl)}>
                              <Wrench class="h-3 w-3" /> Fix
                            </button>
                          {/if}
                        {/if}
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </td>
                    <td>
                      {#if wl.punch_in_lat != null || wl.work_zone_lat != null}
                        <button type="button" class="btn-secondary" onclick={() => toggle(expandedLogs, i)} aria-label="Toggle map">
                          <MapPin class="h-3.5 w-3.5" />
                        </button>
                      {/if}
                    </td>
                  </tr>
                  {#if expandedLogs.has(i)}
                    <tr>
                      <td colspan="6">
                        <div class="h-[220px] w-full overflow-hidden rounded-md border border-gray-200" use:leafletMap={workLogMapParams(wl)}></div>
                      </td>
                    </tr>
                  {/if}
                {:else}
                  <tr><td class="empty" colspan="6">No work logs yet.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <p class="mb-2 text-[13px] font-semibold text-navy">Failed punch attempts</p>

        <div class="sm:hidden">
          {#each d.recent.failed_punch_attempts as a, i (i)}
            <div class="tym-list-card">
              <div class="flex items-start gap-2">
                <div class="min-w-0 flex-1">
                  <p class="tym-list-card-title">{fmtDateTime(a.created_at)}</p>
                  <dl class="tym-list-card-meta">
                    <div><dt>Distance</dt><dd>{(a.distance_m / 1000).toFixed(2)} km</dd></div>
                    <div><dt>Site</dt><dd>{a.zone_name || '—'}</dd></div>
                    <div><dt>Reason</dt><dd>{label(a.reason)}</dd></div>
                  </dl>
                </div>
                <button
                  type="button"
                  class="btn-secondary tym-touch shrink-0 !px-3"
                  onclick={() => toggle(expandedAttempts, i)}
                  aria-label="Toggle map"
                >
                  <MapPin class="h-4 w-4" />
                </button>
              </div>
              {#if expandedAttempts.has(i)}
                <div
                  class="mt-2.5 h-[200px] w-full overflow-hidden rounded-md border border-toyama-cloudy-gray"
                  use:leafletMap={attemptMapParams(a)}
                ></div>
              {/if}
            </div>
          {:else}
            <p class="tym-empty-state">No failed punch attempts.</p>
          {/each}
        </div>

        <div class="tym-table-wrap hidden sm:block">
          <div class="tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Distance</th>
                  <th>Site</th>
                  <th>Reason</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {#each d.recent.failed_punch_attempts as a, i (i)}
                  <tr>
                    <td class="muted">{fmtDateTime(a.created_at)}</td>
                    <td class="muted">{(a.distance_m / 1000).toFixed(2)} km</td>
                    <td class="muted">{a.zone_name || '—'}</td>
                    <td class="muted">{label(a.reason)}</td>
                    <td>
                      <button type="button" class="btn-secondary" onclick={() => toggle(expandedAttempts, i)} aria-label="Toggle map">
                        <MapPin class="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                  {#if expandedAttempts.has(i)}
                    <tr>
                      <td colspan="5">
                        <div class="h-[220px] w-full overflow-hidden rounded-md border border-gray-200" use:leafletMap={attemptMapParams(a)}></div>
                      </td>
                    </tr>
                  {/if}
                {:else}
                  <tr><td class="empty" colspan="5">No failed punch attempts.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================
         LEAVE
         ============================================================ -->
    <section id="person-leave" class="mb-7 scroll-mt-4">
      <div class="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 class="tym-section-title text-base">
            Leave{#if leaveQuery.data} <span class="font-medium text-dark-gray">— {leaveQuery.data.fy_label}</span>{/if}
          </h2>
          <p class="tym-section-sub">Entitlement against what they've taken, and every ledger movement.</p>
        </div>
        {#if canAct}
          <div class="flex flex-wrap gap-2">
            {#if availableOpeningTypes.length > 0}
              <button type="button" class="btn-secondary" onclick={openOpeningBalance}>
                Set opening balance
              </button>
            {/if}
            <button
              type="button"
              class="btn-secondary"
              onclick={openAdjust}
              disabled={!leaveQuery.data || leaveQuery.data.types.length === 0}
            >
              Adjust leave
            </button>
          </div>
        {/if}
      </div>
      {#if leaveQuery.isLoading}
        <div class="tym-loading-state">Loading...</div>
      {:else if leaveQuery.isError}
        <div class="tym-error-state">Could not load leave entitlements.</div>
      {:else if leaveQuery.data}
        <div class="tym-card mb-4 p-5">
          <div class="space-y-2.5">
            {#each leaveQuery.data.types as t (t.leave_type_id)}
              <BarRow label="{t.name} · {t.quota}d quota" value={t.availed} max={Math.max(t.quota, t.availed, 1)} />
            {:else}
              <p class="text-[13px] text-dark-gray">No active leave types configured yet.</p>
            {/each}
          </div>
        </div>

        {#if leaveQuery.data.opening_balances.length > 0}
          <div class="tym-card mb-4 p-5">
            <p class="mb-3 text-[13px] font-semibold text-navy">Opening balances</p>
            <div class="space-y-2">
              {#each leaveQuery.data.opening_balances as entry (entry.id)}
                <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                  <span class="text-[13px] font-medium text-navy">{leaveTypeName(entry.leave_type_id)}</span>
                  <span class="font-heading text-[14px] font-bold tabular-nums text-navy">{entry.days} days</span>
                  <span class="w-full text-[12px] text-dark-gray">{entry.note || 'No note'}</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div class="min-w-0">
            <p class="mb-2 text-[13px] font-semibold text-navy">Recent ledger entries</p>

            <div class="sm:hidden">
              {#each leaveQuery.data.recent_entries as entry (entry.id)}
                <div class="tym-list-card">
                  <div class="flex items-baseline justify-between gap-3">
                    <p class="tym-list-card-title">{fmtDate(entry.entry_date)}</p>
                    <span class="font-heading text-[15px] font-bold tabular-nums {entry.days > 0 ? 'text-navy' : 'text-success'}">
                      {entry.days > 0 ? '+' : ''}{entry.days}
                    </span>
                  </div>
                  <dl class="tym-list-card-meta">
                    <div><dt>Source</dt><dd>{label(entry.source)}</dd></div>
                    <div class="w-full"><dt>Note</dt><dd>{entry.note || '—'}</dd></div>
                  </dl>
                </div>
              {:else}
                <p class="tym-empty-state">No leave ledger entries yet.</p>
              {/each}
            </div>

            <div class="tym-table-wrap hidden sm:block">
              <div class="tym-table-scroll">
                <table class="tym-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Days</th>
                      <th>Source</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each leaveQuery.data.recent_entries as entry (entry.id)}
                      <tr>
                        <td class="muted">{fmtDate(entry.entry_date)}</td>
                        <td class="font-semibold {entry.days > 0 ? 'text-navy' : 'text-success'}">{entry.days > 0 ? '+' : ''}{entry.days}</td>
                        <td class="muted">{label(entry.source)}</td>
                        <td class="muted">{entry.note || '—'}</td>
                      </tr>
                    {:else}
                      <tr><td class="empty" colspan="4">No leave ledger entries yet.</td></tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="tym-card p-5">
            <p class="mb-3 text-[13px] font-semibold text-navy">Leave by reason ({d.days}d)</p>
            <ul class="space-y-1.5 text-[13px]">
              {#each d.leave.by_reason_category as row (row.category)}
                <li class="flex justify-between">
                  <span class="text-dark-gray">{label(row.category)}</span>
                  <span class="font-semibold text-navy">{row.count} ({row.days}d)</span>
                </li>
              {:else}
                <li class="text-dark-gray">No leave in this window.</li>
              {/each}
            </ul>
          </div>
        </div>
      {/if}
    </section>

    <!-- ============================================================
         REQUESTS
         ============================================================ -->
    <section id="person-requests" class="mb-7 scroll-mt-4">
      <h2 class="tym-section-title mb-1 text-base">Requests</h2>
      <p class="tym-section-sub mb-3">Everything they've filed in this window.</p>

      {#if canAct && !isSelf}
        <div class="tym-card mb-4 p-4 sm:p-5">
          <p class="mb-1 text-[13px] font-semibold text-navy">Record leave</p>
          <p class="mb-3 text-[12px] text-dark-gray">Records the fact immediately as approved.</p>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label class="min-w-0">
              <span class="label">Kind</span>
              <select class="input min-h-10 w-full" bind:value={requestForm.type}>
                <option value="leave">Leave</option>
                <option value="late">Late coming</option>
                <option value="early_leaving">Early departure</option>
              </select>
            </label>
            <label class="min-w-0">
              <span class="label">Start date</span>
              <input type="date" class="input min-h-10 w-full" bind:value={requestForm.start_date} />
            </label>
            {#if requestForm.type === 'leave' && !requestForm.half_day}
              <label class="min-w-0">
                <span class="label">End date</span>
                <input type="date" class="input min-h-10 w-full" bind:value={requestForm.end_date} />
              </label>
            {/if}
            {#if requestForm.type === 'leave'}
              <label class="min-w-0">
                <span class="label">Leave type</span>
                <select class="input min-h-10 w-full" bind:value={requestForm.leave_type_id}>
                  {#each leaveQuery.data?.types ?? [] as t (t.leave_type_id)}
                    <option value={t.leave_type_id}>{t.name}</option>
                  {/each}
                </select>
              </label>
            {/if}
            <label class="min-w-0 sm:col-span-2 lg:col-span-3">
              <span class="label">Reason</span>
              <input type="text" class="input min-h-10 w-full" bind:value={requestForm.reason} placeholder="What HR is recording" />
            </label>
            {#if requestForm.type === 'leave'}
              <label class="flex min-h-10 cursor-pointer items-center gap-2 self-end text-[13px] font-medium text-navy">
                <input type="checkbox" class="h-5 w-5" bind:checked={requestForm.half_day} /> Half day
              </label>
            {/if}
          </div>
          {#if requestError}<p class="mt-2 text-[12.5px] text-danger">{requestError}</p>{/if}
          <button type="button" class="btn-primary tym-touch mt-3 w-full sm:w-auto" disabled={recordingRequest} onclick={recordRequest}>
            {recordingRequest ? 'Recording...' : 'Record and approve'}
          </button>
        </div>
      {/if}

      {#if editingRequest}
        <div id="request-date-editor" class="tym-card mb-4 scroll-mt-20 border border-toyama-teal/30 p-4 sm:p-5">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-[13px] font-semibold text-navy">Edit {label(editingRequest.type)} dates</p>
            <button type="button" class="btn-secondary tym-touch" onclick={() => (editingRequest = null)}>Cancel</button>
          </div>
          <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label>
              <span class="label">Start date</span>
              <input type="date" class="input min-h-10 w-full" bind:value={editRequestStart} />
            </label>
            {#if editingRequest.type === 'leave' && !editingRequest.half_day}
              <label>
                <span class="label">End date</span>
                <input type="date" class="input min-h-10 w-full" bind:value={editRequestEnd} />
              </label>
            {/if}
          </div>
          {#if editRequestError}<p class="mt-2 text-[12.5px] text-danger">{editRequestError}</p>{/if}
          <button type="button" class="btn-primary tym-touch mt-3 w-full sm:w-auto" disabled={editingRequestSaving} onclick={saveRequestDates}>
            {editingRequestSaving ? 'Saving...' : 'Save dates'}
          </button>
        </div>
      {/if}

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        <div class="min-w-0">
          <div class="sm:hidden">
            {#each d.recent.hr_requests as r, i (i)}
              <div class="tym-list-card">
                <div class="flex items-start justify-between gap-3">
                  <p class="tym-list-card-title">{label(r.type)}</p>
                  <StatusBadge kind={badgeKind(r.status)} label={label(r.status)} />
                </div>
                <dl class="tym-list-card-meta">
                  <div>
                    <dt>Date(s)</dt>
                    <dd>{fmtDate(r.start_date)}{r.end_date && r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ''}</dd>
                  </div>
                  <div class="w-full"><dt>Reason</dt><dd>{r.reason || '—'}</dd></div>
                </dl>
                {#if canAct && !isSelf && r.type !== 'other'}
                  <button type="button" class="btn-secondary tym-touch mt-3 w-full" onclick={() => openRequestDateEdit(r)}>Edit dates</button>
                {/if}
              </div>
            {:else}
              <p class="tym-empty-state">No requests yet.</p>
            {/each}
          </div>

          <div class="tym-table-wrap hidden sm:block">
            <div class="tym-table-scroll">
              <table class="tym-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date(s)</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {#each d.recent.hr_requests as r, i (i)}
                    <tr>
                      <td class="font-medium text-navy">{label(r.type)}</td>
                      <td class="muted"
                        >{fmtDate(r.start_date)}{r.end_date && r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ''}</td
                      >
                      <td><StatusBadge kind={badgeKind(r.status)} label={label(r.status)} /></td>
                      <td class="muted">{r.reason || '—'}</td>
                      <td>
                        {#if canAct && !isSelf && r.type !== 'other'}
                          <button type="button" class="btn-secondary tym-touch whitespace-nowrap" onclick={() => openRequestDateEdit(r)}>Edit dates</button>
                        {/if}
                      </td>
                    </tr>
                  {:else}
                    <tr><td class="empty" colspan="5">No requests yet.</td></tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="tym-card p-5">
          <p class="mb-3 text-[13px] font-semibold text-navy">By type</p>
          <ul class="space-y-1.5 text-[13px]">
            {#each d.requests.by_type as row (row.key)}
              <li class="flex justify-between">
                <span class="text-dark-gray">{label(row.key)}</span><span class="font-semibold text-navy">{row.count}</span>
              </li>
            {:else}
              <li class="text-dark-gray">No requests in this window.</li>
            {/each}
            <li class="flex justify-between border-t border-toyama-cloudy-gray pt-1.5">
              <span class="text-dark-gray">Late</span><span class="font-semibold text-navy">{d.requests.late_count}</span>
            </li>
            <li class="flex justify-between">
              <span class="text-dark-gray">Early leaving</span
              ><span class="font-semibold text-navy">{d.requests.early_leaving_count}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ============================================================
         PROFILE
         ============================================================ -->
    <section id="person-profile" class="mb-7 scroll-mt-4">
      <h2 class="tym-section-title mb-1 text-base">Profile</h2>
      <p class="tym-section-sub mb-3">Contact details and employment record. Changes are audit-logged.</p>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div class="tym-card p-5">
          <p class="mb-3 text-[13px] font-semibold text-navy">Contact &amp; emergency</p>
          {#if profileQuery.isLoading}
            <p class="tym-loading-state !p-0 text-left">Loading...</p>
          {:else}
            <div class="space-y-3">
              <div>
                <div class="label">Phone</div>
                <input type="text" class="input" aria-label="Phone" bind:value={contactForm.phone} />
              </div>
              <div>
                <div class="label">Personal email</div>
                <input type="email" class="input" aria-label="Personal email" bind:value={contactForm.personal_email} />
              </div>
              <div>
                <div class="label">Address</div>
                <textarea class="input" rows="2" aria-label="Address" bind:value={contactForm.address}></textarea>
              </div>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div class="label">Emergency contact name</div>
                  <input type="text" class="input" aria-label="Emergency contact name" bind:value={contactForm.emergency_contact_name} />
                </div>
                <div>
                  <div class="label">Emergency contact phone</div>
                  <input type="text" class="input" aria-label="Emergency contact phone" bind:value={contactForm.emergency_contact_phone} />
                </div>
              </div>
              <div class="flex justify-end">
                <button type="button" class="btn-primary" disabled={savingContact} onclick={saveContact}>
                  {savingContact ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          {/if}
        </div>

        <div class="tym-card p-5">
          <p class="mb-3 text-[13px] font-semibold text-navy">Employment info</p>
          {#if profileQuery.isLoading}
            <p class="tym-loading-state !p-0 text-left">Loading...</p>
          {:else}
            <div class="space-y-3">
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div class="label">Designation</div>
                  <input type="text" class="input" aria-label="Designation" bind:value={employmentForm.designation} />
                </div>
                <div>
                  <div class="label">Department</div>
                  <input type="text" class="input" aria-label="Department" bind:value={employmentForm.department} />
                </div>
              </div>
              <div>
                <div class="label">Gender</div>
                <select class="input" aria-label="Gender" bind:value={employmentForm.gender}>
                  <option value="">— not set —</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div class="label">Date of joining</div>
                  <input type="date" class="input" aria-label="Date of joining" bind:value={employmentForm.date_of_joining} />
                </div>
                <div>
                  <div class="label">Employment type</div>
                  <select class="input" aria-label="Employment type" bind:value={employmentForm.employment_type}>
                    <option value="">— not set —</option>
                    <option value="full_time">Full-time</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
              </div>
              <div>
                <div class="label">Work location</div>
                <input type="text" class="input" aria-label="Work location" bind:value={employmentForm.work_location} />
              </div>
              <div class="flex justify-end">
                <button type="button" class="btn-primary" disabled={savingEmployment} onclick={saveEmployment}>
                  {savingEmployment ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          {/if}
        </div>
      </div>

      {#if profileQuery.data?.admin}
        {@const admin = profileQuery.data.admin}
        <div class="tym-card mt-4 p-5">
          <p class="mb-3 text-[13px] font-semibold text-navy">
            Government ID &amp; compensation <span class="tym-badge tym-badge--danger">admin only</span>
          </p>
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div class="space-y-3">
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div class="label">Aadhaar number</div>
                  <input type="text" class="input" aria-label="Aadhaar number" bind:value={adminForm.aadhaar_number} />
                </div>
                <div>
                  <div class="label">PAN number</div>
                  <input type="text" class="input" aria-label="PAN number" bind:value={adminForm.pan_number} />
                </div>
              </div>
              <div>
                <div class="label">Annual CTC</div>
                <input type="number" step="0.01" min="0" class="input" aria-label="Annual CTC" bind:value={adminForm.annual_ctc_amount} />
              </div>
              <div>
                <div class="label">Compensation note</div>
                <textarea class="input" rows="2" aria-label="Compensation note" bind:value={adminForm.compensation_note}></textarea>
              </div>
              <div class="flex justify-end">
                <button type="button" class="btn-primary" disabled={savingAdmin} onclick={saveAdmin}>
                  {savingAdmin ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            <div>
              <p class="mb-2 text-[13px] font-semibold text-navy">Documents</p>
              <div class="mb-3 flex flex-wrap items-center gap-2">
                <input bind:this={fileInputEl} type="file" class="input !w-auto flex-1" aria-label="Document file" onchange={onFileChange} accept=".pdf,.jpg,.jpeg,.png,.webp" />
                <input type="text" class="input !w-auto flex-1" placeholder="Label (optional)" aria-label="Document label" bind:value={uploadLabel} />
                <button type="button" class="btn-secondary shrink-0" disabled={!uploadFile || uploading} onclick={uploadDocument}>
                  <Upload class="h-3.5 w-3.5" /> {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
              <ul class="space-y-2">
                {#each admin.documents as doc (doc.id)}
                  <li class="flex items-center justify-between gap-2 rounded-lg border border-toyama-cloudy-gray p-2.5 text-[13px]">
                    <div class="min-w-0 flex-1">
                      <p class="truncate font-medium text-navy">{doc.filename}</p>
                      <p class="truncate text-dark-gray">{doc.label || 'No label'} &middot; {(doc.size_bytes / 1024).toFixed(0)} KB</p>
                    </div>
                    <div class="flex shrink-0 gap-1.5">
                      <button type="button" class="btn-secondary tym-touch !px-3" onclick={() => downloadDocument(doc)} aria-label="Download" title="Download">
                        <Download class="h-3.5 w-3.5" />
                      </button>
                      <button type="button" class="btn-danger-solid tym-touch !px-3" onclick={() => deleteDocument(doc.id, doc.filename)} aria-label="Delete" title="Delete">
                        <Trash2 class="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                {:else}
                  <li class="text-[13px] text-dark-gray">No documents uploaded yet.</li>
                {/each}
              </ul>
            </div>
          </div>
        </div>
      {/if}
    </section>

    <!-- ============================================================
         DEVICES & ACCESS — the three recovery actions live here, together,
         instead of being scattered across Sites > Devices and Directory.
         Every one of them is also enforced server-side (hr may act on
         employees only; admin may act on anyone) — see hr_users.go.
         ============================================================ -->
    <section id="person-account" class="mb-7 scroll-mt-4">
      <h2 class="tym-section-title mb-1 text-base">Devices &amp; access</h2>
      <p class="tym-section-sub mb-3">
        Their bound device, sign-in state, and the three recovery actions HR can take on their account.
      </p>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div class="tym-card p-5">
          <p class="mb-3 text-[13px] font-semibold text-navy">Account state</p>
          <dl class="space-y-2.5 text-[13px]">
            <div class="flex items-start justify-between gap-3">
              <dt class="text-dark-gray">Bound device</dt>
              <dd class="text-right">
                {#if device?.registered}
                  <span class="tym-badge tym-badge--approved">Registered</span>
                  {#if device.user_agent}
                    <p class="mt-1 max-w-[220px] truncate text-[11.5px] text-dark-gray" title={device.user_agent}>
                      {device.user_agent}
                    </p>
                  {/if}
                {:else}
                  <span class="tym-badge tym-badge--pending">None</span>
                {/if}
              </dd>
            </div>
            <div class="flex items-start justify-between gap-3">
              <dt class="text-dark-gray">Device last seen</dt>
              <dd class="font-medium text-navy">{device?.last_seen_at ? fmtDateTime(device.last_seen_at) : '—'}</dd>
            </div>
            <div class="flex items-start justify-between gap-3">
              <dt class="text-dark-gray">Two-factor</dt>
              <dd>
                {#if d.profile.mfa_set}
                  <span class="tym-badge tym-badge--approved">Enrolled</span>
                {:else}
                  <span class="tym-badge tym-badge--pending">Not set up</span>
                {/if}
              </dd>
            </div>
            <div class="flex items-start justify-between gap-3">
              <dt class="text-dark-gray">Last login</dt>
              <dd class="text-right font-medium text-navy">
                {d.profile.last_login_at ? fmtDateTime(d.profile.last_login_at) : 'Never'}
                {#if d.profile.last_login_device}
                  <p class="mt-0.5 max-w-[220px] truncate text-[11.5px] font-normal text-dark-gray" title={d.profile.last_login_device}>
                    {d.profile.last_login_device}
                  </p>
                {/if}
              </dd>
            </div>
          </dl>
        </div>

        <div class="tym-card p-5">
          <p class="mb-1 text-[13px] font-semibold text-navy">HR actions</p>
          {#if !canAct}
            <p class="text-[13px] text-dark-gray">
              This is {targetRole === 'admin' ? 'an admin' : 'an HR'} account — only an admin can reset its password, device
              or two-factor.
            </p>
          {:else}
            <p class="mb-3 text-[12.5px] text-dark-gray">
              Each of these is recorded in the audit trail below.
              {#if isSelf}<span class="font-semibold">Careful — this is your own account.</span>{/if}
            </p>
            <!-- Each row stacks on a phone so its button is a full-width
                 44px target rather than a 60px-wide one wedged against the
                 description it belongs to. -->
            <div class="space-y-2">
              <div
                class="flex flex-col items-stretch gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div class="min-w-0">
                  <p class="flex items-center gap-1.5 text-[13px] font-medium text-navy"><KeyRound class="h-3.5 w-3.5" /> Reset password</p>
                  <p class="text-[11.5px] text-dark-gray">Set a new password and share it privately.</p>
                </div>
                <button type="button" class="btn-secondary tym-touch shrink-0" onclick={openResetPassword}>Reset</button>
              </div>

              <div
                class="flex flex-col items-stretch gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div class="min-w-0">
                  <p class="flex items-center gap-1.5 text-[13px] font-medium text-navy"><Smartphone class="h-3.5 w-3.5" /> Reset device</p>
                  <p class="text-[11.5px] text-dark-gray">Unbind their phone so they can register a new one.</p>
                </div>
                <button
                  type="button"
                  class="tym-touch shrink-0 {confirmingResetDevice ? 'btn-danger-solid' : 'btn-secondary'}"
                  disabled={resettingDevice || !device?.registered}
                  onclick={resetDevice}
                  onblur={() => (confirmingResetDevice = false)}
                >
                  {#if resettingDevice}Resetting...{:else if confirmingResetDevice}Confirm?{:else}Reset{/if}
                </button>
              </div>

              <div
                class="flex flex-col items-stretch gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div class="min-w-0">
                  <p class="flex items-center gap-1.5 text-[13px] font-medium text-navy"><ShieldOff class="h-3.5 w-3.5" /> Reset two-factor</p>
                  <p class="text-[11.5px] text-dark-gray">Clear their authenticator so they re-enrol at next login.</p>
                </div>
                <button
                  type="button"
                  class="tym-touch shrink-0 {confirmingResetMFA ? 'btn-danger-solid' : 'btn-secondary'}"
                  disabled={resettingMFA || !d.profile.mfa_set}
                  onclick={resetMFA}
                  onblur={() => (confirmingResetMFA = false)}
                >
                  {#if resettingMFA}Resetting...{:else if confirmingResetMFA}Confirm?{:else}Reset{/if}
                </button>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </section>

    <!-- Mobile action dock — the three recovery actions are the reason HR
         opens this page on a phone, and they sit ~2000px down it. Docked,
         they are one tap away from anywhere on the record. Both destructive
         ones keep their two-step confirm (they arm here and in the section
         at once, since it is the same state), and the password reset opens
         its dialog, so a stray thumb can't actually change anything. -->
    {#if canAct}
      <div class="tym-action-dock sm:hidden">
        <div class="flex items-center gap-2">
          <span class="tym-selection-count shrink-0">HR actions</span>
          <div class="ml-auto flex min-w-0 items-center gap-1.5">
            <button type="button" class="btn-secondary tym-touch !px-2.5 !text-[12.5px]" onclick={openResetPassword}>
              <KeyRound class="h-3.5 w-3.5" /> Password
            </button>
            <button
              type="button"
              class="tym-touch !px-2.5 !text-[12.5px] {confirmingResetDevice ? 'btn-danger-solid' : 'btn-secondary'}"
              disabled={resettingDevice || !device?.registered}
              onclick={resetDevice}
            >
              <Smartphone class="h-3.5 w-3.5" />
              {confirmingResetDevice ? 'Confirm?' : 'Device'}
            </button>
            <button
              type="button"
              class="tym-touch !px-2.5 !text-[12.5px] {confirmingResetMFA ? 'btn-danger-solid' : 'btn-secondary'}"
              disabled={resettingMFA || !d.profile.mfa_set}
              onclick={resetMFA}
            >
              <ShieldOff class="h-3.5 w-3.5" />
              {confirmingResetMFA ? 'Confirm?' : '2FA'}
            </button>
          </div>
        </div>
      </div>
    {/if}

    <!-- ============================================================
         BOARD — admin-only (hidden entirely for hr, whose call to
         GET /api/admin/users/:id/board 403s; see boardQuery's enabled guard).
         ============================================================ -->
    {#if session.isAdmin && boardQuery.data}
      {@const b = boardQuery.data}
      <section id="person-board" class="mb-7 scroll-mt-4">
        <div class="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 class="tym-section-title text-base">Board <span class="tym-badge tym-badge--info">read-only</span></h2>
            <p class="tym-section-sub">Their personal kanban, as they see it.</p>
          </div>
          <p class="tym-section-sub">{b.no_work_day_count} no-work day{b.no_work_day_count === 1 ? '' : 's'} logged</p>
        </div>
        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          {#each BOARD_COLUMNS as c (c.id)}
            <div class="tym-card p-3">
              <p class="mb-2 text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">{c.label}</p>
              <div class="space-y-1.5">
                {#each b.cards.filter((card) => card.col === c.id) as card (card.id)}
                  <p class="text-[12.5px] text-navy">{card.title}</p>
                {:else}
                  <p class="text-[12px] text-dark-gray">—</p>
                {/each}
              </div>
            </div>
          {/each}
        </div>
      </section>
    {/if}

    <!-- ============================================================
         HISTORY
         ============================================================ -->
    <section id="person-history" class="mb-7 scroll-mt-4">
      <h2 class="tym-section-title mb-1 text-base">History</h2>
      <p class="tym-section-sub mb-3">Every HR action taken on this account, append-only.</p>
      {#if auditQuery.isLoading}
        <div class="tym-loading-state">Loading...</div>
      {:else if auditQuery.isError}
        <div class="tym-error-state">Could not load this employee's history.</div>
      {:else if auditQuery.data}
        <div class="sm:hidden">
          {#each auditQuery.data.slice(0, historyVisible) as row (row.id)}
            <div class="tym-list-card">
              <div class="flex items-start justify-between gap-3">
                <span class="tym-badge tym-badge--info">{AUDIT_ACTION_LABEL[row.action] ?? row.action}</span>
                <span class="shrink-0 text-[11.5px] tabular-nums text-dark-gray">{fmtDateTime(row.created_at)}</span>
              </div>
              <p class="mt-1.5 text-[12.5px] text-toyama-heavy-carbon">{humanizeAuditDetails(row.action, row.details)}</p>
              <p class="mt-0.5 text-[11.5px] text-dark-gray">
                by {row.actor_name || (row.actor_user_id ? `#${row.actor_user_id}` : 'System')}
              </p>
            </div>
          {:else}
            <p class="tym-empty-state">No history yet.</p>
          {/each}
        </div>

        <div class="tym-table-wrap hidden sm:block">
          <div class="tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {#each auditQuery.data.slice(0, historyVisible) as row (row.id)}
                  <tr>
                    <td class="muted" title={new Date(row.created_at).toLocaleString()}>{fmtDateTime(row.created_at)}</td>
                    <td class="muted">{row.actor_name || (row.actor_user_id ? `#${row.actor_user_id}` : 'System')}</td>
                    <td><span class="tym-badge tym-badge--info">{AUDIT_ACTION_LABEL[row.action] ?? row.action}</span></td>
                    <td class="muted">{humanizeAuditDetails(row.action, row.details)}</td>
                  </tr>
                {:else}
                  <tr><td class="empty" colspan="4">No history yet.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
        {#if auditQuery.data.length > historyVisible}
          <div class="mt-3 flex justify-center">
            <button type="button" class="btn-secondary" onclick={() => (historyVisible += 15)}>Load more</button>
          </div>
        {/if}
      {/if}
    </section>

    <!-- ============================================================
         CONTINUITY — admin only. Nothing in this block renders, and the
         queries it depends on never fire, for hr (see personQuery/
         continuityUsersQuery's `enabled: session.isAdmin` above).
         ============================================================ -->
    {#if session.isAdmin}
      <section id="person-continuity" class="mb-7 scroll-mt-4">
        <h2 class="tym-section-title mb-1 text-base">Continuity</h2>
        <p class="tym-section-sub mb-3">What they hold, when we last talked to them, and how exposed we are.</p>
        {#if personQuery.isLoading}
          <div class="tym-loading-state">Loading...</div>
        {:else if personQuery.isError}
          <div class="tym-error-state">Could not load continuity data for this person.</div>
        {:else if personQuery.data}
          {@const p = personQuery.data}

          <!-- Someone is leaving -->
          <div class="tym-card mb-4 space-y-3 p-5">
            <p class="text-[13px] font-semibold text-navy">Someone is leaving</p>
            {#if p.notice_status === 'serving_notice' && p.last_working_day}
              {@const lwd = p.last_working_day}
              <div class="rounded-lg border border-toyama-cloudy-gray bg-toyama-light-carbon p-3">
                <p class="text-[13px] font-semibold text-navy">
                  {p.name} is serving notice — last working day {lwd} ({daysRemaining(lwd)} day{daysRemaining(lwd) === 1 ? '' : 's'} remaining).
                </p>
              </div>
              <p class="text-[13px] font-semibold text-navy">Handover checklist</p>
              <div class="space-y-1.5">
                {#each p.actions.filter((a) => a.due_date && a.due_date <= lwd) as a (a.id)}
                  <div class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2">
                    <div class="min-w-[55%] flex-1">
                      <p class="text-[13px] {a.status === 'done' ? 'text-dark-gray line-through' : 'text-navy'}">{a.description}</p>
                      <p class="text-[11px] text-dark-gray">{a.owner_name}{a.due_date ? ` — due ${a.due_date}` : ''}</p>
                    </div>
                    <button type="button" class="btn-secondary tym-touch !px-3 shrink-0" onclick={() => toggleActionDone(a)}>
                      {a.status === 'done' ? 'Reopen' : 'Done'}
                    </button>
                  </div>
                {:else}
                  <p class="text-[13px] text-dark-gray">No checklist items.</p>
                {/each}
              </div>
              <div class="flex justify-end">
                <button type="button" class="btn-danger-solid" onclick={cancelNotice} onblur={() => (confirmingCancelNotice = false)}>
                  {confirmingCancelNotice ? 'Click again to confirm' : 'Cancel notice'}
                </button>
              </div>
            {:else}
              <button type="button" class="btn-secondary" onclick={openNoticeModal}>Set to serving notice</button>
            {/if}
          </div>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <!-- Dates & pay band -->
            <div class="tym-card space-y-4 p-5">
              <p class="text-[13px] font-semibold text-navy">Dates &amp; pay band</p>
              <div>
                <div class="label">Group (continuity)</div>
                <input type="text" class="input" bind:value={continuityForm.department} oninput={() => (continuityFormDirty = true)} />
              </div>
              <div>
                <div class="label">Joined (continuity)</div>
                <input type="date" class="input" bind:value={continuityForm.date_of_joining} onchange={() => (continuityFormDirty = true)} />
              </div>
              <div>
                <div class="label">With us (months)</div>
                <p class="text-[13px] text-dark-gray">{p.with_us_months ?? '—'} <span class="text-[11px]">(worked out from the joining date)</span></p>
              </div>
              <div>
                <div class="label">Last pay rise</div>
                <input type="date" class="input" bind:value={continuityForm.last_increment_date} onchange={() => (continuityFormDirty = true)} />
              </div>
              <div>
                <div class="label">Last role change</div>
                <input type="date" class="input" bind:value={continuityForm.last_role_change_date} onchange={() => (continuityFormDirty = true)} />
              </div>
              <div>
                <div class="label">
                  Pay band position
                  <InfoTip text={CONTINUITY_TOOLTIP.payBandPosition} />
                </div>
                <select class="input" bind:value={continuityForm.pay_band_position} onchange={() => (continuityFormDirty = true)}>
                  {#each PAY_BAND_OPTIONS as opt (opt.value)}
                    <option value={opt.value}>{opt.label}</option>
                  {/each}
                </select>
              </div>
              <div>
                <div class="label">
                  Talk to them by
                  <InfoTip text={CONTINUITY_TOOLTIP.talkToThemBy} />
                </div>
                <p class="text-[13px] text-dark-gray">{p.talk_to_them_by || '—'} <span class="text-[11px]">(worked out from the joining/conversation dates)</span></p>
              </div>
              <div class="flex justify-end">
                <button type="button" class="btn-primary" disabled={!continuityFormDirty || savingContinuityDetails} onclick={saveContinuityDetails}>
                  {savingContinuityDetails ? 'Saving...' : 'Save details'}
                </button>
              </div>
            </div>

            <!-- OKRs -->
            <div class="tym-card space-y-3 p-5">
              <p class="text-[13px] font-semibold text-navy">OKRs</p>
              {#each [{ label: 'OKRs they do', refs: p.okrs_main }, { label: 'OKRs they back up', refs: p.okrs_backup }, { label: 'Can also do', refs: p.okrs_can_do }] as group (group.label)}
                <div>
                  <p class="text-[13px] font-semibold text-navy">{group.label}</p>
                  {#if group.refs.length === 0}
                    <p class="text-[12px] text-dark-gray">None.</p>
                  {:else}
                    <div class="space-y-1">
                      {#each group.refs as ref (ref.id)}
                        <button type="button" class="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-[13px] text-navy hover:bg-toyama-light-carbon" onclick={() => okrRefClick(ref)}>
                          <StatusLamp tone={LAMP_TONE[ref.colour]} />
                          {ref.name}
                        </button>
                      {/each}
                    </div>
                  {/if}
                </div>
              {/each}
            </div>

            <!-- What they hold -->
            <div class="tym-card space-y-3 p-5">
              <div class="flex items-center justify-between">
                <p class="text-[13px] font-semibold text-navy">
                  What they hold
                  <InfoTip text={CONTINUITY_TOOLTIP.custody} />
                </p>
                <button type="button" class="btn-secondary" onclick={openNewCustody}>
                  <Plus class="h-3.5 w-3.5" /> Add
                </button>
              </div>
              <div class="space-y-1.5">
                {#each p.custody as cu (cu.id)}
                  <div class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2">
                    <div class="min-w-[55%] flex-1">
                      <p class="text-[13px] text-navy">{cu.name} <span class="tym-badge">{CUSTODY_TYPE_LABEL[cu.type]}</span></p>
                      <p class="text-[11px] text-dark-gray">
                        {cu.holder_name}{cu.second_holder_name ? ` · second: ${cu.second_holder_name}` : ''}{cu.last_verified ? ` · verified ${cu.last_verified}` : ''}
                      </p>
                    </div>
                    <div class="flex shrink-0 gap-1.5">
                      <button type="button" class="btn-secondary tym-touch !px-3" onclick={() => openEditCustody(cu)}>Edit</button>
                      <button
                        type="button"
                        class={confirmingDeleteCustodyId === cu.id ? 'btn-danger-solid tym-touch !px-3' : 'btn-secondary tym-touch !px-3'}
                        onclick={() => deleteCustody(cu)}
                        onblur={() => (confirmingDeleteCustodyId = null)}
                      >
                        {confirmingDeleteCustodyId === cu.id ? 'Confirm?' : 'Delete'}
                      </button>
                    </div>
                  </div>
                {:else}
                  <p class="text-[13px] text-dark-gray">Nothing on record.</p>
                {/each}
              </div>
            </div>

            <!-- Conversations -->
            <div class="tym-card space-y-3 p-5">
              <div class="flex items-center justify-between">
                <p class="text-[13px] font-semibold text-navy">Conversations</p>
                <button type="button" class="btn-secondary" onclick={openConversation}>
                  <Plus class="h-3.5 w-3.5" /> Log a conversation
                </button>
              </div>
              <div class="sm:hidden">
                {#each p.conversations as c (c.id)}
                  <div class="tym-list-card">
                    <div class="flex items-baseline justify-between gap-3">
                      <p class="tym-list-card-title">{c.date}</p>
                      <span class="tym-badge tym-badge--info">{c.type}</span>
                    </div>
                    <dl class="tym-list-card-meta">
                      <div><dt>Conducted by</dt><dd>{c.conducted_by_name}</dd></div>
                      <div class="w-full"><dt>Notes</dt><dd>{c.notes || '—'}</dd></div>
                    </dl>
                  </div>
                {:else}
                  <p class="tym-empty-state">No conversations logged yet.</p>
                {/each}
              </div>

              <div class="tym-table-wrap hidden sm:block">
                <div class="tym-table-scroll">
                  <table class="tym-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Conducted by</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {#each p.conversations as c (c.id)}
                        <tr>
                          <td class="muted">{c.date}</td>
                          <td class="muted">{c.type}</td>
                          <td class="muted">{c.conducted_by_name}</td>
                          <td class="muted">{c.notes || '—'}</td>
                        </tr>
                      {:else}
                        <tr><td class="empty" colspan="4">No conversations logged yet.</td></tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="tym-card space-y-3 p-5 lg:col-span-2">
              <div class="flex items-center justify-between">
                <p class="text-[13px] font-semibold text-navy">Actions</p>
                <button type="button" class="btn-secondary" onclick={openNewAction}>
                  <Plus class="h-3.5 w-3.5" /> Add action
                </button>
              </div>
              <div class="space-y-1.5">
                {#each p.actions as a (a.id)}
                  <div class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2">
                    <div class="min-w-[55%] flex-1">
                      <p class="text-[13px] {a.status === 'done' ? 'text-dark-gray line-through' : 'text-navy'}">{a.description}</p>
                      <p class="text-[11px] text-dark-gray">{a.owner_name}{a.due_date ? ` — due ${a.due_date}` : ''}</p>
                    </div>
                    <div class="flex shrink-0 gap-1.5">
                      <button type="button" class="btn-secondary tym-touch !px-3" onclick={() => openEditAction(a)}>Edit</button>
                      <button type="button" class="btn-secondary tym-touch !px-3" onclick={() => toggleActionDone(a)}>
                        {a.status === 'done' ? 'Reopen' : 'Done'}
                      </button>
                    </div>
                  </div>
                {:else}
                  <p class="text-[13px] text-dark-gray">No actions yet.</p>
                {/each}
              </div>
            </div>
          </div>
        {/if}
      </section>
    {/if}
  {/if}
</div>

<!-- Where a punch happened — one definition, rendered by both the desktop
     table cell and the mobile card. A remote (field-work) punch has no site:
     it carries the pasted place instead. -->
{#snippet workLogWhere(wl: HRAnalyticsRecentWorkLog)}
  {#if wl.remote}
    <span class="tym-badge tym-badge--info">Remote</span>
    {#if wl.maps_link}
      <a class="ml-1 text-toyama-teal underline" href={wl.maps_link} target="_blank" rel="noopener noreferrer">
        {wl.location_name}
      </a>
    {:else}
      <span class="ml-1">{wl.location_name}</span>
    {/if}
    {#if wl.location_area && wl.location_area !== '—'}
      <span class="muted"> · {wl.location_area}</span>
    {/if}
  {:else}
    <span class="muted">{wl.work_zone_name || '—'}</span>
  {/if}
{/snippet}

{#if showManualAttendance}
  <Modal title="Mark present" onClose={() => (showManualAttendance = false)}>
    <div class="space-y-4">
      <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
        This is an HR-entered attendance record, not a GPS-verified employee punch. The selected site is recorded by HR only.
      </p>
      <div class="grid gap-3 sm:grid-cols-2">
        <div><label class="label" for="manual-punch-in">Start time ({DISPLAY_TZ})</label><input id="manual-punch-in" class="input" type="datetime-local" bind:value={manualPunchIn} /></div>
        <div><label class="label" for="manual-punch-out">End time (optional)</label><input id="manual-punch-out" class="input" type="datetime-local" bind:value={manualPunchOut} /></div>
      </div>
      <div>
        <label class="label" for="manual-work-zone">Site (optional)</label>
        <select id="manual-work-zone" class="input" bind:value={manualWorkZoneID}>
          <option value="">No site recorded</option>
          {#each (workZonesQuery.data ?? []).filter((zone) => zone.active) as zone (zone.id)}<option value={String(zone.id)}>{zone.name}</option>{/each}
        </select>
      </div>
      <p class="text-[12px] text-dark-gray">Past attendance records must include an end time.</p>
      <div><label class="label" for="manual-reason">Reason</label><textarea id="manual-reason" class="input min-h-24" maxlength="500" bind:value={manualReason}></textarea></div>
      {#if manualAttendanceError}<div class="tym-error-state">{manualAttendanceError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2"><button type="button" class="btn-secondary" onclick={() => (showManualAttendance = false)}>Cancel</button><button type="button" class="btn-primary" disabled={savingManualAttendance} onclick={submitManualAttendance}>{savingManualAttendance ? 'Saving...' : 'Record attendance'}</button></div>
    {/snippet}
  </Modal>
{/if}

{#if showResetPassword}
  <Modal title="Reset password" onClose={() => (showResetPassword = false)}>
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">
        This sets a new password immediately. Share it over a private channel — they should change it on their next login.
      </p>
      <div>
        <div class="label">New password</div>
        <input type="text" class="input" aria-label="New password" autocomplete="off" bind:value={newPassword} />
      </div>
      {#if resetPasswordError}<div class="tym-error-state">{resetPasswordError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showResetPassword = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={resettingPassword} onclick={submitResetPassword}>
          {resettingPassword ? 'Resetting...' : 'Reset password'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if fixingLog}
  <Modal title="Fix punch-out" onClose={() => (fixingLog = null)}>
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">
        Punched in {fmtDateTime(fixingLog.punch_in)}. Set the real punch-out time — this restores the hours worked,
        stops this occurrence from counting toward warnings, and reverses any leave day it deducted.
      </p>
      <div>
        <div class="label">Real punch-out</div>
        <input type="datetime-local" class="input" aria-label="Real punch-out" bind:value={fixPunchOut} />
      </div>
      {#if fixError}<div class="tym-error-state">{fixError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (fixingLog = null)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={fixing} onclick={submitFix}>
          {fixing ? 'Fixing...' : 'Fix'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showOpeningBalance}
  <Modal title="Set opening balance" onClose={() => (showOpeningBalance = false)}>
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">Record leave already taken before this ledger was introduced. This entry cannot be edited or entered again for the same leave type.</p>
      <div>
        <div class="label">Leave type</div>
        <select class="input" aria-label="Opening balance leave type" bind:value={openingLeaveTypeId}>
          {#each availableOpeningTypes as t (t.leave_type_id)}
            <option value={t.leave_type_id}>{t.name}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Days already taken</div>
        <input type="number" min="0" step="0.5" class="input" aria-label="Opening balance days" bind:value={openingDays} />
      </div>
      <div>
        <div class="label">Note <span class="font-normal text-dark-gray">(optional)</span></div>
        <input type="text" class="input" aria-label="Opening balance note" bind:value={openingNote} placeholder="Source or period covered" />
      </div>
      {#if openingError}<div class="tym-error-state">{openingError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showOpeningBalance = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={savingOpening} onclick={submitOpeningBalance}>
          {savingOpening ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showAdjust}
  <Modal title="Adjust leave" onClose={() => (showAdjust = false)}>
    <div class="space-y-4">
      <div>
        <div class="label">Leave type</div>
        <select class="input" aria-label="Leave type" bind:value={adjustLeaveTypeId}>
          {#each leaveQuery.data?.types ?? [] as t (t.leave_type_id)}
            <option value={t.leave_type_id}>{t.name}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Days (+ credit / − debit)</div>
        <input type="number" step="0.5" class="input" aria-label="Days" bind:value={adjustDays} />
      </div>
      <div>
        <div class="label">Note</div>
        <input type="text" class="input" aria-label="Note" bind:value={adjustNote} placeholder="Reason for this adjustment" />
      </div>
      {#if adjustError}<div class="tym-error-state">{adjustError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showAdjust = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={adjusting} onclick={submitAdjust}>
          {adjusting ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showConversation}
  <Modal title="Log a conversation" onClose={() => (showConversation = false)}>
    <div class="space-y-4">
      <p class="text-[12px] text-dark-gray">{CONTINUITY_TOOLTIP.talkToThemBy}</p>
      <div>
        <div class="label">Date</div>
        <input type="date" class="input" bind:value={conversationForm.date} />
      </div>
      <div>
        <div class="label">Type</div>
        <select class="input" bind:value={conversationForm.type}>
          {#each CONVERSATION_TYPE_OPTIONS as opt (opt.value)}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Conducted by</div>
        <select class="input" bind:value={conversationForm.conducted_by}>
          <option value={0}>Select a person</option>
          {#each continuityUsersQuery.data || [] as u (u.id)}
            <option value={u.id}>{u.name || u.email}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Notes</div>
        <textarea class="input" rows="2" bind:value={conversationForm.notes}></textarea>
      </div>
      {#if conversationError}<div class="tym-error-state">{conversationError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showConversation = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={savingConversation} onclick={submitConversation}>
          {savingConversation ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showActionModal}
  <Modal title={editingAction ? 'Edit action' : 'Add action'} onClose={() => (showActionModal = false)}>
    <div class="space-y-4">
      <div>
        <div class="label">Description</div>
        <input type="text" class="input" bind:value={actionForm.description} />
      </div>
      <div>
        <div class="label">Owner</div>
        <select class="input" bind:value={actionForm.owner_id}>
          <option value={0}>Select an owner</option>
          {#each continuityUsersQuery.data || [] as u (u.id)}
            <option value={u.id}>{u.name || u.email}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Due date</div>
        <input type="date" class="input" bind:value={actionForm.due_date} />
      </div>
      {#if editingAction}
        <div>
          <div class="label">Status</div>
          <select class="input" bind:value={actionForm.status}>
            <option value="open">Open</option>
            <option value="done">Done</option>
          </select>
        </div>
      {/if}
      {#if actionError}<div class="tym-error-state">{actionError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showActionModal = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={savingAction} onclick={submitAction}>
          {savingAction ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showCustodyModal}
  <Modal title={editingCustody ? 'Edit custody item' : 'Add custody item'} onClose={() => (showCustodyModal = false)}>
    <div class="space-y-4">
      <div>
        <div class="label">Type</div>
        <select class="input" bind:value={custodyForm.type}>
          {#each CUSTODY_TYPE_OPTIONS as opt (opt.value)}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Name</div>
        <input type="text" class="input" bind:value={custodyForm.name} placeholder="e.g. Github org owner" />
      </div>
      <div>
        <div class="label">Second person</div>
        <select class="input" bind:value={custodyForm.second_holder_id}>
          <option value={0}>None</option>
          {#each continuityUsersQuery.data || [] as u (u.id)}
            <option value={u.id}>{u.name || u.email}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Last verified</div>
        <input type="date" class="input" bind:value={custodyForm.last_verified} />
      </div>
      {#if custodyError}<div class="tym-error-state">{custodyError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showCustodyModal = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={savingCustody} onclick={submitCustody}>
          {savingCustody ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showNoticeModal}
  <Modal title="Set to serving notice" onClose={() => (showNoticeModal = false)}>
    <div class="space-y-4">
      <div>
        <div class="label">Last working day</div>
        <input type="date" class="input" bind:value={noticeLastWorkingDay} />
      </div>
      <p class="text-[12px] text-dark-gray">
        This creates a handover checklist (OKR hand-overs, proof tests, custody transfers, and an exit conversation) due by the last
        working day, and notifies admins.
      </p>
      {#if noticeError}<div class="tym-error-state">{noticeError}</div>{/if}
    </div>
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary tym-touch flex-1 sm:flex-none" onclick={() => (showNoticeModal = false)}>Cancel</button>
        <button type="button" class="btn-primary tym-touch flex-1 sm:flex-none" disabled={savingNotice} onclick={submitNotice}>
          {savingNotice ? 'Saving...' : 'Confirm'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}
